import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router";
import PageMeta from "../../components/common/PageMeta";
import PageContainer from "../../components/common/PageContainer";
import PageHeader from "../../components/common/PageHeader";
import { useApi } from "../../hooks/useApi";
import { useBrandId } from "../../hooks/useBrandId";
import { useCompanyId } from "../../hooks/useCompanyId";
import CopyRowButton from "../../components/alerts/CopyRowButton";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Box,
  Typography,
  MenuItem,
  Collapse,
  IconButton,
  TextField,
  Menu,
  Checkbox,
  FormControlLabel,
  Divider,
  Badge,
  TableSortLabel,
  Button,
} from '@mui/material';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import FilterListIcon from '@mui/icons-material/FilterList';
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";
import * as Sentry from "@sentry/react";
import MultiSelectFilter from "../../components/common/MultiSelectFilter";
import SearchableMultiSelectFilter from "../../components/common/SearchableMultiSelectFilter";
import CopyableValue from "../../components/common/CopyableValue";
import CopyableCardNumber from "../../components/alerts/CopyableCardNumber";
import { filterPanelSx } from "../../components/common/filterStyles";
import { VerifiNotice, VerifiNoticesResponse, VerifiNoticeMerchantFilterItem, VerifiNoticeDescriptorFilterItem } from "../../types/verifiNotice";
import { HasPermission } from "../../functions/auth";
import { PERMISSIONS } from "../../constants/permissions";
import { formatCurrency, formatAmountWithoutCurrency } from "../../utils/formatCurrency";
import { formatAgeLabel } from "../../utils/formatAge";
import ActivityLog from "./ActivityLog";
import AgeChip from "../../components/alerts/AgeChip";
import MappingWarningChip from "../../components/alerts/MappingWarningChip";
import DeflectionChip from "../../components/alerts/DeflectionChip";
import VerifiNoticesCsvDialog from "../../components/alerts/VerifiNoticesCsvDialog";

const COLUMNS_STORAGE_KEY = 'ce_notices_visible_columns_v1';

// Deflection outcome maps to the CE notice's case_event: NEW = deflected, FAILED = not.
const DEFLECTION_FILTER_OPTIONS = [
  { value: 'NEW', label: 'Deflected' },
  { value: 'FAILED', label: 'Failed' },
];
const DEFLECTION_FILTER_VALUES = DEFLECTION_FILTER_OPTIONS.map(o => o.value);

interface ColumnConfig {
  id: string;
  label: string;
  isDefault: boolean;
  sortField?: string;
}

const allColumns: ColumnConfig[] = [
  { id: 'deflection', label: 'Deflection', isDefault: true },
  { id: 'case_id', label: 'CE Case ID', isDefault: true },
  { id: 'linked_insight_id', label: 'Linked Insight ID', isDefault: true },
  { id: 'company_name', label: 'Company', isDefault: false },
  { id: 'brand_name', label: 'Brand', isDefault: false },
  { id: 'merchant_name', label: 'Merchant', isDefault: true },
  { id: 'payment_descriptor', label: 'Descriptor', isDefault: true },
  { id: 'amount', label: 'Amount', isDefault: true },
  { id: 'case_date', label: 'Date', isDefault: true, sortField: 'case_date' },
  { id: 'card_number', label: 'Card Number', isDefault: false },
  { id: 'acquirer_bin', label: 'Acquirer BIN', isDefault: false },
  { id: 'card_acceptor_id', label: 'Card Acceptor ID', isDefault: false },
  { id: 'age', label: 'Age', isDefault: false },
];

const defaultColumnIds = allColumns.filter(c => c.isDefault).map(c => c.id);
const validColumnIds = allColumns.map(c => c.id);

const getStoredColumns = (): string[] => {
  try {
    const stored = localStorage.getItem(COLUMNS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed.filter((id: string) => validColumnIds.includes(id));
      }
    }
  } catch (e) {
    console.error('Failed to parse stored columns', e);
  }
  return defaultColumnIds;
};

const saveStoredColumns = (columnIds: string[]) => {
  localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(columnIds));
};

const formatDateTime = (dateString: string | Date | null | undefined) => {
  if (!dateString) return '--';
  const dateStr = dateString instanceof Date ? dateString.toISOString() : dateString;
  return dayjs.utc(dateStr).format('MMM DD, YYYY HH:mm');
};

function CeNoticeRow({ notice, visibleColumns }: { notice: VerifiNotice; visibleColumns: string[] }) {
  const [open, setOpen] = useState(false);

  const renderCell = (columnId: string) => {
    switch (columnId) {
      case 'deflection':
        return <TableCell key={columnId}><DeflectionChip caseEvent={notice.case_event} /></TableCell>;
      case 'case_id':
        return (
          <TableCell key={columnId}>
            {notice.case_id ? (
              <CopyableValue
                text={notice.case_id}
                copyTitle="Copy case ID"
                revealOnHover
              />
            ) : (
              '--'
            )}
          </TableCell>
        );
      case 'linked_insight_id':
        return (
          <TableCell key={columnId}>
            {notice.linked_insight_id ? (
              <CopyableValue
                text={notice.linked_insight_id}
                copyTitle="Copy insight ID"
                revealOnHover
              />
            ) : (
              '--'
            )}
          </TableCell>
        );
      case 'merchant_name':
        return <TableCell key={columnId}>{notice.merchant_name || '--'}</TableCell>;
      case 'payment_descriptor':
        return <TableCell key={columnId}>{notice.payment_descriptor || '--'}</TableCell>;
      case 'amount': {
        const cents = notice.transaction_amount;
        if (cents === null || cents === undefined) return <TableCell key={columnId}>--</TableCell>;
        return (
          <TableCell key={columnId}>
            <CopyableValue
              text={formatCurrency(cents, notice.transaction_currency || 'USD', true)}
              copyValue={formatAmountWithoutCurrency(cents, notice.transaction_currency || 'USD')}
              copyTitle="Copy amount"
              revealOnHover
            />
          </TableCell>
        );
      }
      case 'case_date':
        return <TableCell key={columnId}>{formatDateTime(notice.case_date)}</TableCell>;
      case 'card_number':
        return (
          <TableCell key={columnId}>
            <CopyableCardNumber cardBin={notice.card_bin} cardLast4={notice.card_last4} revealOnHover />
          </TableCell>
        );
      case 'acquirer_bin':
        return <TableCell key={columnId}>{notice.acquirer_bin || '--'}</TableCell>;
      case 'card_acceptor_id':
        return <TableCell key={columnId}>{notice.card_acceptor_id || '--'}</TableCell>;
      case 'company_name':
        return <TableCell key={columnId}>{notice.company_name || '--'}</TableCell>;
      case 'brand_name':
        return <TableCell key={columnId}>{notice.brand_name || '--'}</TableCell>;
      case 'age':
        return (
          <TableCell key={columnId}>
            <AgeChip ageHours={notice.age_hours} expiredThreshold={Infinity} warningThreshold={Infinity} />
          </TableCell>
        );
      default:
        return null;
    }
  };

  return (
    <React.Fragment>
      <TableRow hover onClick={() => setOpen(!open)} sx={{ '& > *': { borderBottom: 'unset' }, cursor: 'pointer' }}>
        {allColumns.filter(c => visibleColumns.includes(c.id)).map(c => renderCell(c.id))}
        <TableCell />
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={visibleColumns.length + 1}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Notice Details
                </Typography>
                <CopyRowButton row={notice} columnSet="verifi-notice" />
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2, maxWidth: 1200 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">CE Case ID</Typography>
                  {notice.case_id ? (
                    <Box sx={{ mt: 0.25 }}>
                      <CopyableValue
                        text={notice.case_id}
                        copyTitle="Copy case ID"
                        textSx={{ fontSize: '1rem', fontWeight: 500 }}
                      />
                    </Box>
                  ) : (
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>--</Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Linked Insight ID</Typography>
                  {notice.linked_insight_id ? (
                    <Box sx={{ mt: 0.25 }}>
                      <CopyableValue
                        text={notice.linked_insight_id}
                        copyTitle="Copy insight ID"
                        textSx={{ fontSize: '1rem', fontWeight: 500 }}
                      />
                    </Box>
                  ) : (
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>--</Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Deflection</Typography>
                  <DeflectionChip caseEvent={notice.case_event} />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Company</Typography>
                  {notice.company_name ? (
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{notice.company_name}</Typography>
                  ) : (
                    <MappingWarningChip label="Not linked to any company" />
                  )}
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Merchant</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>{notice.merchant_name || '--'}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Descriptor</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>{notice.payment_descriptor || '--'}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Amount</Typography>
                  {notice.transaction_amount === null || notice.transaction_amount === undefined ? (
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>--</Typography>
                  ) : (
                    <Box sx={{ mt: 0.25 }}>
                      <CopyableValue
                        text={formatCurrency(notice.transaction_amount, notice.transaction_currency || 'USD', true)}
                        copyValue={formatAmountWithoutCurrency(notice.transaction_amount, notice.transaction_currency || 'USD')}
                        copyTitle="Copy amount"
                        textSx={{ fontSize: '1rem', fontWeight: 500 }}
                      />
                    </Box>
                  )}
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Card Number</Typography>
                  <Box sx={{ mt: 0.25 }}>
                    <CopyableCardNumber
                      cardBin={notice.card_bin}
                      cardLast4={notice.card_last4}
                      textSx={{ fontSize: '1rem', fontWeight: 500 }}
                    />
                  </Box>
                </Box>
                {notice.acquirer_bin && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">Acquirer BIN</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{notice.acquirer_bin}</Typography>
                  </Box>
                )}
                {notice.card_acceptor_id && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">Card Acceptor ID</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{notice.card_acceptor_id}</Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="body2" color="text.secondary">Case Date</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>{formatDateTime(notice.case_date)}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Age</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>{notice.age_hours != null ? formatAgeLabel(notice.age_hours) : '--'}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Created At</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>{formatDateTime(notice.created_at)}</Typography>
                </Box>
              </Box>

              <ActivityLog logs={notice.logs} />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

export default function CeNotices() {
  const { get, loading, error: apiError } = useApi();
  const { companyId } = useCompanyId();
  const { brandId } = useBrandId();
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();

  const initialStartDate = urlSearchParams.get("startDate") ? dayjs.utc(urlSearchParams.get("startDate")) : dayjs.utc().subtract(7, 'day');
  const initialEndDate = urlSearchParams.get("endDate") ? dayjs.utc(urlSearchParams.get("endDate")) : dayjs.utc();
  const initialCaseId = urlSearchParams.get("caseId") || '';
  const initialNoCompany = urlSearchParams.get("noCompany") === 'true';
  const initialPage = urlSearchParams.get("page") ? Math.max(0, parseInt(urlSearchParams.get("page")!, 10)) : 0;
  const initialRowsPerPage = urlSearchParams.get("rowsPerPage") ? parseInt(urlSearchParams.get("rowsPerPage")!, 10) : 25;
  const initialSortBy = urlSearchParams.get("sortBy") || 'case_date';
  const initialSortOrder = (urlSearchParams.get("sortOrder") === 'asc' ? 'asc' : 'desc') as 'asc' | 'desc';
  const initialCaseEvents = (urlSearchParams.get("caseEvent") || '').split(',').filter(v => DEFLECTION_FILTER_VALUES.includes(v));
  const initialMerchantIds = (urlSearchParams.get("merchantIds") || '').split(',').filter(Boolean);
  const initialDescriptorIds = (urlSearchParams.get("descriptorIds") || '').split(',').filter(Boolean);

  const [startDate, setStartDate] = useState<Dayjs | null>(initialStartDate);
  const [endDate, setEndDate] = useState<Dayjs | null>(initialEndDate);
  const [caseId, setCaseId] = useState<string>(initialCaseId);
  const [noCompany, setNoCompany] = useState(initialNoCompany);
  const [caseEvents, setCaseEvents] = useState<string[]>(initialCaseEvents);
  const [merchantIds, setMerchantIds] = useState<string[]>(initialMerchantIds);
  const [descriptorIds, setDescriptorIds] = useState<string[]>(initialDescriptorIds);
  const [noticeMerchants, setNoticeMerchants] = useState<VerifiNoticeMerchantFilterItem[]>([]);
  const [noticeDescriptors, setNoticeDescriptors] = useState<VerifiNoticeDescriptorFilterItem[]>([]);
  const [noticesData, setNoticesData] = useState<VerifiNoticesResponse | null>(null);
  const [page, setPage] = useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
  const [sortBy, setSortBy] = useState<string>(initialSortBy);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(initialSortOrder);
  const [pageError, setPageError] = useState<string | null>(null);

  const [visibleColumns, setVisibleColumns] = useState<string[]>(getStoredColumns);
  const [columnMenuAnchor, setColumnMenuAnchor] = useState<null | HTMLElement>(null);
  const columnMenuOpen = Boolean(columnMenuAnchor);
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);
  const [startPickerOpen, setStartPickerOpen] = useState(false);
  const [endPickerOpen, setEndPickerOpen] = useState(false);

  const hasActiveFilters = caseId !== '' || noCompany || caseEvents.length > 0 || merchantIds.length > 0 || descriptorIds.length > 0;
  const [filtersExpanded, setFiltersExpanded] = useState(hasActiveFilters);
  const activeFiltersCount = (caseId !== '' ? 1 : 0) + (noCompany ? 1 : 0) + (caseEvents.length > 0 ? 1 : 0) + (merchantIds.length > 0 ? 1 : 0) + (descriptorIds.length > 0 ? 1 : 0);

  const merchantOptions = noticeMerchants.map((merchant) => ({ value: String(merchant.merchant_id), label: merchant.merchant_name }));
  const descriptorOptions = noticeDescriptors.map((desc) => ({ value: String(desc.descriptor_id), label: desc.descriptor_name }));

  const handleColumnMenuOpen = (event: React.MouseEvent<HTMLElement>) => setColumnMenuAnchor(event.currentTarget);
  const handleColumnMenuClose = () => setColumnMenuAnchor(null);

  const handleColumnToggle = (columnId: string) => {
    setVisibleColumns(prev => {
      const newColumns = prev.includes(columnId)
        ? prev.filter(id => id !== columnId)
        : [...prev, columnId];
      saveStoredColumns(newColumns);
      return newColumns;
    });
  };

  const handleResetColumns = () => {
    setVisibleColumns(defaultColumnIds);
    saveStoredColumns(defaultColumnIds);
  };

  const handleShowAllColumns = () => {
    setVisibleColumns(validColumnIds);
    saveStoredColumns(validColumnIds);
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPage(0);
  };

  useEffect(() => {
    const params = new URLSearchParams(urlSearchParams);

    if (startDate) params.set("startDate", startDate.format("YYYY-MM-DD"));
    else params.delete("startDate");

    if (endDate) params.set("endDate", endDate.format("YYYY-MM-DD"));
    else params.delete("endDate");

    if (caseId) params.set("caseId", caseId);
    else params.delete("caseId");

    if (noCompany) params.set("noCompany", "true");
    else params.delete("noCompany");

    if (caseEvents.length > 0) params.set("caseEvent", caseEvents.join(','));
    else params.delete("caseEvent");

    if (page > 0) params.set("page", page.toString());
    else params.delete("page");

    if (rowsPerPage > 0) params.set("rowsPerPage", rowsPerPage.toString());
    else params.delete("rowsPerPage");

    if (sortBy && sortBy !== 'case_date') params.set("sortBy", sortBy);
    else params.delete("sortBy");

    if (sortOrder !== 'desc') params.set("sortOrder", sortOrder);
    else params.delete("sortOrder");

    if (merchantIds.length > 0) params.set("merchantIds", merchantIds.join(','));
    else params.delete("merchantIds");

    if (descriptorIds.length > 0) params.set("descriptorIds", descriptorIds.join(','));
    else params.delete("descriptorIds");

    setUrlSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, caseId, noCompany, caseEvents, merchantIds, descriptorIds, page, rowsPerPage, sortBy, sortOrder]);

  const fetchNoticeMerchants = useCallback(async () => {
    try {
      const params = companyId ? `?companyId=${companyId}` : '';
      const response = await get<{ merchants: VerifiNoticeMerchantFilterItem[] }>(`/api/alerts/verifi-notices/merchants${params}`);
      setNoticeMerchants(response.merchants);
    } catch (err) {
      console.error('Failed to fetch CE merchants:', err);
      Sentry.captureException(err);
    }
  }, [get, companyId]);

  const fetchNoticeDescriptors = useCallback(async () => {
    try {
      const params = companyId ? `?companyId=${companyId}` : '';
      const response = await get<{ descriptors: VerifiNoticeDescriptorFilterItem[] }>(`/api/alerts/verifi-notices/descriptors${params}`);
      setNoticeDescriptors(response.descriptors);
    } catch (err) {
      console.error('Failed to fetch CE descriptors:', err);
      Sentry.captureException(err);
    }
  }, [get, companyId]);

  useEffect(() => {
    fetchNoticeMerchants();
  }, [fetchNoticeMerchants]);

  useEffect(() => {
    fetchNoticeDescriptors();
  }, [fetchNoticeDescriptors]);

  const fetchNotices = useCallback(async () => {
    if (!startDate || !endDate) return;
    setPageError(null);

    try {
      const params = new URLSearchParams({
        caseType: 'CE_NOTICE',
        startDate: startDate.format('YYYY-MM-DD'),
        endDate: endDate.format('YYYY-MM-DD'),
        ...(caseId && { caseId }),
        ...(noCompany && { noCompany: 'true' }),
        ...(caseEvents.length > 0 && { caseEvent: caseEvents.join(',') }),
        ...(merchantIds.length > 0 && { merchantIds: merchantIds.join(',') }),
        ...(descriptorIds.length > 0 && { descriptorIds: descriptorIds.join(',') }),
        ...(companyId && { companyIds: companyId.toString() }),
        ...(brandId && { brandId: String(brandId) }),
        page: (page + 1).toString(),
        limit: rowsPerPage.toString(),
        sortBy,
        sortOrder,
      });

      const response = await get<VerifiNoticesResponse>(`/api/alerts/verifi-notices?${params.toString()}`);
      setNoticesData(response);
    } catch (err) {
      console.error('Failed to fetch CE notices:', err);
      setPageError(err instanceof Error ? err.message : 'Unknown error');
      Sentry.captureException(err);
      setNoticesData(null);
    }
  }, [get, startDate, endDate, caseId, noCompany, caseEvents, merchantIds, descriptorIds, companyId, brandId, page, rowsPerPage, sortBy, sortOrder]);

  useEffect(() => {
    fetchNotices();
  }, [fetchNotices]);

  const handleStartDateChange = (date: Dayjs | null) => { setStartDate(date); setPage(0); };
  const handleEndDateChange = (date: Dayjs | null) => { setEndDate(date); setPage(0); };
  const handleChangePage = (_event: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const visibleSortedColumns = useMemo(
    () => allColumns.filter(c => visibleColumns.includes(c.id)),
    [visibleColumns],
  );

  return (
    <div>
      <PageMeta
        title="Chargemont | Alerts - C.E. Notices"
        description="Chargemont | Alerts - C.E. Notices"
      />
      <PageContainer>
        <PageHeader
          title="C.E. Notices"
          action={
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <HasPermission permission={PERMISSIONS.ALERTS_CSV}>
                <Button
                  variant="contained"
                  onClick={() => setCsvDialogOpen(true)}
                  sx={{
                    backgroundColor: '#465fff',
                    textTransform: 'none',
                    '&:hover': { backgroundColor: '#3641f5' },
                  }}
                >
                  Export CSV
                </Button>
              </HasPermission>
            </Box>
          }
        />

        {/* Filters */}
        <Box className="mb-6" sx={filterPanelSx}>
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" flexWrap="wrap" gap={2}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box display="flex" alignItems="center" gap={1}>
                  <DatePicker
                    label="Start"
                    value={startDate}
                    onChange={handleStartDateChange}
                    open={startPickerOpen}
                    onOpen={() => setStartPickerOpen(true)}
                    onClose={() => setStartPickerOpen(false)}
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: { width: "fit-content" },
                        onClick: () => setStartPickerOpen(true),
                      },
                    }}
                  />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>—</Typography>
                  <DatePicker
                    label="End"
                    value={endDate}
                    onChange={handleEndDateChange}
                    open={endPickerOpen}
                    onOpen={() => setEndPickerOpen(true)}
                    onClose={() => setEndPickerOpen(false)}
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: { width: "fit-content" },
                        onClick: () => setEndPickerOpen(true),
                      },
                    }}
                  />
                </Box>
              </LocalizationProvider>
            </Box>

            <Box display="flex" alignItems="center" flexWrap="wrap" gap={2}>
              <MultiSelectFilter
                label="Deflection"
                options={DEFLECTION_FILTER_OPTIONS}
                value={caseEvents}
                onChange={(values) => { setCaseEvents(values); setPage(0); }}
              />
              <IconButton
                onClick={() => setFiltersExpanded(!filtersExpanded)}
                size="small"
                sx={{
                  border: '1px solid',
                  borderColor: filtersExpanded ? 'primary.main' : 'divider',
                  borderRadius: 1,
                  backgroundColor: filtersExpanded ? 'primary.50' : 'transparent',
                  '&:hover': { backgroundColor: filtersExpanded ? 'primary.100' : 'action.hover' },
                }}
              >
                <Badge
                  badgeContent={activeFiltersCount}
                  color="primary"
                  sx={{ '& .MuiBadge-badge': { fontSize: 10, height: 16, minWidth: 16 } }}
                >
                  <FilterListIcon fontSize="small" sx={{ color: filtersExpanded ? 'primary.main' : 'text.secondary' }} />
                </Badge>
              </IconButton>
            </Box>
          </Box>

          <Collapse in={filtersExpanded}>
            <Box
              display="flex"
              alignItems="center"
              flexWrap="wrap"
              gap={2}
              sx={{ px: 2, pb: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}
            >
              <SearchableMultiSelectFilter
                label="Merchant"
                options={merchantOptions}
                value={merchantIds}
                width={200}
                searchPlaceholder="Search merchants…"
                showSelectAllClearAll
                onChange={(values) => { setMerchantIds(values); setPage(0); }}
              />
              <SearchableMultiSelectFilter
                label="Descriptor"
                options={descriptorOptions}
                value={descriptorIds}
                width={200}
                searchPlaceholder="Search descriptors…"
                showSelectAllClearAll
                onChange={(values) => { setDescriptorIds(values); setPage(0); }}
              />
              <TextField
                size="small"
                label="CE Case ID"
                value={caseId}
                onChange={(e) => { setCaseId(e.target.value); setPage(0); }}
                sx={{ minWidth: 200 }}
                placeholder="Search..."
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={noCompany}
                    onChange={(e) => { setNoCompany(e.target.checked); setPage(0); }}
                  />
                }
                label="Without company"
                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
              />
            </Box>
          </Collapse>
        </Box>

        {(apiError || pageError) && (
          <Box className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            Error: {apiError || pageError}
          </Box>
        )}

        {/* Data Table */}
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {visibleSortedColumns.map(column => (
                    <TableCell key={column.id} sx={{ whiteSpace: 'nowrap' }}>
                      {column.sortField ? (
                        <TableSortLabel
                          active={sortBy === column.sortField}
                          direction={sortBy === column.sortField ? sortOrder : 'asc'}
                          onClick={() => handleSort(column.sortField!)}
                        >
                          {column.label}
                        </TableSortLabel>
                      ) : (
                        column.label
                      )}
                    </TableCell>
                  ))}
                  <TableCell sx={{ textAlign: 'center' }}>
                    <IconButton
                      onClick={handleColumnMenuOpen}
                      size="small"
                      sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
                    >
                      <SettingsOutlinedIcon fontSize="medium" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length + 1} align="center">
                      <Typography variant="body2" color="text.secondary">Loading...</Typography>
                    </TableCell>
                  </TableRow>
                ) : noticesData?.notices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length + 1} align="center">
                      <Typography variant="body2" color="text.secondary">No CE notices found.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  noticesData?.notices.map((notice) => (
                    <CeNoticeRow key={notice.verifi_notice_id} notice={notice} visibleColumns={visibleColumns} />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {noticesData && (
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              component="div"
              count={noticesData.pagination.totalItems}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </Paper>
      </PageContainer>

      {/* Column Settings Menu */}
      <Menu
        anchorEl={columnMenuAnchor}
        open={columnMenuOpen}
        onClose={handleColumnMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { maxHeight: 400, width: 250 } } }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Configure Columns</Typography>
        </Box>
        <Divider />
        <Box sx={{ px: 1 }}>
          {allColumns.map((column) => (
            <MenuItem key={column.id} dense sx={{ py: 0.5 }} onClick={() => handleColumnToggle(column.id)}>
              <Checkbox checked={visibleColumns.includes(column.id)} size="small" sx={{ py: 0, mr: 1 }} />
              <Typography variant="body2">{column.label}</Typography>
            </MenuItem>
          ))}
        </Box>
        <Divider />
        <MenuItem onClick={handleResetColumns} dense>
          <Typography variant="body2" color="primary">Reset to Default</Typography>
        </MenuItem>
        <MenuItem onClick={handleShowAllColumns} dense>
          <Typography variant="body2" color="primary">All Columns</Typography>
        </MenuItem>
      </Menu>

      <VerifiNoticesCsvDialog
        open={csvDialogOpen}
        onClose={() => setCsvDialogOpen(false)}
        companyId={companyId}
        caseType="CE_NOTICE"
        title="C.E. Notices"
        showVerifiPortalFormat={false}
      />
    </div>
  );
}
